import { useLinkProps, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import type { FC, ReactNode } from 'react';
import useTargetCharacterSection from 'src/hooks/characters/useTargetCharacterSection';
import useTargetPlayerSection from 'src/hooks/players/useTargetPlayerSection';
import PlayerBadges, { type TUserOrganization } from '../0200_player_badges';
import Tab from '../0200_tab';

interface IProps {
  characterOnly?: boolean;
  characterIcon?: ({ characterId }: { characterId?: number }) => ReactNode;
  playerId?: number;
  playerName?: string;
  playerFirstName?: string | null;
  playerLastName?: string;
  playerHasPreferredName?: boolean;
  characterId?: number;
  characterName?: string;
  userOrganization?: TUserOrganization;
  onClick?: ({
    playerId,
    playerName,
    characterId,
    characterName,
  }: {
    playerId?: number;
    playerName?: string;
    characterId?: number;
    characterName?: string;
  }) => void;
}

const SearchResult: FC<IProps> = ({
  characterOnly,
  characterIcon,
  playerId,
  playerName,
  playerFirstName,
  playerLastName,
  playerHasPreferredName,
  characterId,
  characterName,
  userOrganization,
  onClick,
}) => {
  const { organizationSlug } = useParams({ strict: false });
  const { targetCharacterSection } = useTargetCharacterSection();
  const { targetPlayerSection } = useTargetPlayerSection();
  const linkToPlayer = useLinkProps({
    to: targetPlayerSection,
    params: {
      organizationSlug: String(organizationSlug),
      playerId: String(playerId),
    },
  });
  const linkToCharacter = useLinkProps({
    to: targetCharacterSection,
    params: {
      organizationSlug: String(organizationSlug),
      playerId: String(playerId),
      characterId: String(characterId),
    },
  });

  return (
    <Tab
      isCompact
      label={
        <div className="flex">
          <div className="w-12 pr-2 text-right">
            {!characterOnly && <div>{playerId}</div>}
            {!characterOnly &&
              characterName &&
              (characterIcon ? (
                characterIcon({ characterId })
              ) : (
                <div>&raquo;</div>
              ))}
            {characterOnly &&
              characterName &&
              characterIcon &&
              characterIcon({ characterId })}
          </div>
          <div className="w-48 text-left">
            {!characterOnly && (
              <div className={clsx(characterName && 'text-juno-gray-700')}>
                <div>
                  {playerName}
                  <PlayerBadges userOrganization={userOrganization} />
                </div>
                {playerHasPreferredName && (
                  <div className="opacity-50">
                    a.k.a. {[playerFirstName, playerLastName].join(' ')}
                  </div>
                )}
              </div>
            )}
            {characterName && <div>{characterName}</div>}
          </div>
        </div>
      }
      className="py-1"
      highlightMode="vertical"
      to={
        onClick
          ? undefined
          : characterId
            ? linkToCharacter.href
            : linkToPlayer.href
      }
      onClick={() =>
        onClick?.({ playerId, characterId, playerName, characterName })
      }
    />
  );
};

export default SearchResult;
